import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faStar, faPlus, faTrash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import './FeedbackModal.css';

const API = process.env.REACT_APP_API_BASE_URL;

// Same status codes as before
const STATUS = [
  { value: 0, label: 'Class completed' },
  { value: 1, label: 'Student absent' },
  { value: 2, label: 'Student left' },
  { value: 3, label: 'Technical issues' },
];

const PaidFeedbackModal = ({ feedback, onClose, onSubmitSuccess }) => {
  // the last class of the package: the last one scheduled AND no sessions left to schedule
  // (students who book week by week still have sessions waiting: no PTM yet)
  const isLastSession = feedback.sessionsremain === 1 && !(Number(feedback.notScheduled) > 0);

  const [form, setForm] = useState({
    IdClassStatus: 0,
    Projects: [{ ModuleId: '', ProjectId: '' }],
    Rating: 0,
    Details: '',
    PTMHappened: isLastSession,
    PTMNotes: '',
  });
  const [modules, setModules] = useState([]);
  const [nextCourse, setNextCourse] = useState(''); // optional: the course the student chose for after this one
  const [progress, setProgress] = useState(null); // { <moduleId>: { name, level, type, done: [ids] } }
  const [paidTypes, setPaidTypes] = useState(null); // { standard: bool, mastery: bool }: which kind of sessions the family paid

  // which projects of each course the student already did (to see when this class finishes a course)
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/teacher/StudentProfile/courseprogress/${feedback.IdStudent}`)
      .then((res) => {
        if (!alive) return;
        setProgress(res.data?.modules || {});
        setPaidTypes(res.data?.paid || null);
      })
      .catch(() => alive && setProgress({}));
    return () => {
      alive = false;
    };
  }, [feedback.IdStudent]);
  const [projectLists, setProjectLists] = useState([]); // projects for each row
  const [loadingModules, setLoadingModules] = useState(true);
  const [loadingRow, setLoadingRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({}); // shown under each field: projects, rating, notes, ptm

  const completed = form.IdClassStatus === 0;
  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setError('');
    setFieldErrors({});
  };

  // ---------- Modules (same endpoint) ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/Curriculum/modules/${feedback.IdGradesPlans}`);
        if (!cancelled) setModules(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching modules:', err);
        if (!cancelled) setError('We couldn’t load the modules. Close and try again.');
      } finally {
        if (!cancelled) setLoadingModules(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [feedback.IdGradesPlans]);

  // Escape closes (not while sending)
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !submitting && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  // ---------- Projects of a module (same endpoint) ----------
  const loadProjects = async (moduleId, index) => {
    setLoadingRow(index);
    try {
      const res = await axios.get(`${API}/api/teacher/Curriculum/paid/${moduleId}/${feedback.IdGradesPlans}`);
      setProjectLists((prev) => {
        const next = [...prev];
        next[index] = res.data?.courses || [];
        return next;
      });
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('We couldn’t load the projects for this module.');
    } finally {
      setLoadingRow(null);
    }
  };

  const setRow = (index, patch) =>
    update({ Projects: form.Projects.map((p, i) => (i === index ? { ...p, ...patch } : p)) });

  const onModule = (index, value) => {
    const moduleId = value ? parseInt(value, 10) : '';
    setRow(index, { ModuleId: moduleId, ProjectId: '' });
    setProjectLists((prev) => {
      const next = [...prev];
      next[index] = [];
      return next;
    });
    if (moduleId) loadProjects(moduleId, index);
  };

  const addRow = () => form.Projects.length < 2 && update({ Projects: [...form.Projects, { ModuleId: '', ProjectId: '' }] });

  const removeRow = (index) => {
    if (form.Projects.length <= 1) return;
    update({ Projects: form.Projects.filter((_, i) => i !== index) });
    setProjectLists((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- Submit (same checks + same payload) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const missingProject = form.Projects.some((p) => !p.ModuleId || !p.ProjectId);
    const errs = {};
    if (completed && missingProject) errs.projects = 'Choose a module and a project for each row.';
    if (completed && form.Rating === 0) errs.rating = 'Please rate the class.';
    if (!form.Details.trim()) errs.notes = completed ? 'Please add your class notes.' : 'Please write what happened.';
    if (completed && isLastSession && form.PTMHappened && !form.PTMNotes.trim()) errs.ptm = 'Please add the PTM notes.';
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      // bring the first field with a problem into view
      setTimeout(() => document.querySelector('.rmfb .rmfb-field-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
      return undefined;
    }

    setSubmitting(true);
    setError('');
    try {
      // the next course first: if it can't be saved, nothing is sent and the teacher can try again
      if (nextCourse) {
        await axios.put(`${API}/api/teacher/StudentProfile/nextcourse/${feedback.IdStudent}`, { moduleId: nextCourse });
      }
      await axios.post(`${API}/api/teacher/submitpaidform`, {
        ClassDateTime: feedback.ClassDateTime,
        IdSchedule: feedback.IdSchedule,
        IdTeacher: feedback.IdTeacher,
        IdStudent: feedback.IdStudent,
        IdClassStatus: form.IdClassStatus,
        Projects: form.Projects,
        Rating: form.Rating,
        Details: form.Details,
        PTMHappened: isLastSession ? form.PTMHappened : null,
        PTMNotes: isLastSession ? form.PTMNotes : null,
      });
      onSubmitSuccess();
      onClose();
    } catch (err) {
      console.error('Error submitting paid feedback:', err);
      setError(err.response?.data?.error || 'We couldn’t send the feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const name = [feedback.FirstName, feedback.LastName].filter(Boolean).join(' ');
  const when = new Date(feedback.ClassDateTime);

  // a Mastery course picked for a student who follows Standard courses (nothing about payments for teachers)
  const masteryWarning = (row) => {
    if (!completed || !progress || !paidTypes || !row.ModuleId) return null;
    const m = progress[String(row.ModuleId)];
    if (m && m.type === 'mastery' && !paidTypes.mastery) return m.name;
    return null;
  };
  const FieldError = ({ k }) =>
    fieldErrors[k] ? (
      <p className="rmfb-field-error" role="alert">
        <FontAwesomeIcon icon={faTriangleExclamation} /> {fieldErrors[k]}
      </p>
    ) : null;

  // the courses this class finishes (their 8 / 16 / 24 is reached with the projects chosen above)
  const finishing = (() => {
    if (!completed || !progress) return [];
    const out = [];
    const byModule = {};
    form.Projects.forEach((p) => {
      if (!p.ModuleId || !p.ProjectId) return;
      const k = String(p.ModuleId);
      (byModule[k] = byModule[k] || new Set()).add(String(p.ProjectId));
    });
    Object.entries(byModule).forEach(([k, chosen]) => {
      const m = progress[k];
      if (!m || !m.level) return;
      const before = new Set(m.done.map(String));
      const after = new Set([...before, ...chosen]);
      if (before.size < m.level && after.size >= m.level) out.push(m.name);
    });
    return out;
  })();

  return (
    <div className="rmfb-overlay" onClick={() => !submitting && onClose()}>
      <form
        className="rmfb"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rmfb-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <header className="rmfb-head">
          <div>
            <span className="rmfb-kind is-paid">Paid class</span>
            <h2 id="rmfb-title">{name || 'Class feedback'}</h2>
            {!Number.isNaN(when.getTime()) && (
              <p>
                {when.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })} ·{' '}
                {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
          <button type="button" className="rmfb-close" onClick={onClose} disabled={submitting} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        <div className="rmfb-body">
          {/* status */}
          <fieldset className="rmfb-field">
            <legend>How did the class go?</legend>
            <div className="rmfb-chips">
              {STATUS.map((s) => (
                <label key={s.value} className={`rmfb-chip ${form.IdClassStatus === s.value ? 'is-on' : ''}`}>
                  <input
                    type="radio"
                    name="IdClassStatus"
                    checked={form.IdClassStatus === s.value}
                    onChange={() => update({ IdClassStatus: s.value })}
                    disabled={submitting}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </fieldset>

          {completed && (
            <>
              {/* projects */}
              <fieldset className="rmfb-field">
                <legend>Projects done in this class</legend>
                {form.Projects.map((p, i) => (
                  <div key={i} className="rmfb-row">
                    <label className="rmfb-sr" htmlFor={`rmfb-module-${i}`}>
                      Module {i + 1}
                    </label>
                    <select
                      id={`rmfb-module-${i}`}
                      className="rmfb-input"
                      value={p.ModuleId}
                      onChange={(e) => onModule(i, e.target.value)}
                      disabled={submitting || loadingModules}
                    >
                      <option value="">{loadingModules ? 'Loading modules…' : 'Choose module'}</option>
                      {modules.map((m) => (
                        <option key={m.Id} value={m.Id}>
                          {m.ModuleName}
                        </option>
                      ))}
                    </select>

                    <label className="rmfb-sr" htmlFor={`rmfb-project-${i}`}>
                      Project {i + 1}
                    </label>
                    <select
                      id={`rmfb-project-${i}`}
                      className="rmfb-input"
                      value={p.ProjectId}
                      onChange={(e) => setRow(i, { ProjectId: e.target.value ? parseInt(e.target.value, 10) : '' })}
                      disabled={submitting || !p.ModuleId || loadingRow === i}
                    >
                      <option value="">{loadingRow === i ? 'Loading projects…' : 'Choose project'}</option>
                      {(projectLists[i] || []).map((c) => (
                        <option key={c.Id} value={c.Id}>
                          {c.CourseName}
                        </option>
                      ))}
                    </select>

                    {i > 0 && (
                      <button type="button" className="rmfb-icon-btn" onClick={() => removeRow(i)} disabled={submitting} aria-label="Remove this project">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                    {masteryWarning(p) && (
                      <p className="rmfb-row-note">
                        <FontAwesomeIcon icon={faTriangleExclamation} /> <b>{masteryWarning(p)}</b> is a Mastery course, and this student follows
                        Standard courses. Check with the admin before starting a Mastery course with them.
                      </p>
                    )}
                  </div>
                ))}
                {form.Projects.length < 2 && (
                  <button type="button" className="rmfb-link" onClick={addRow} disabled={submitting}>
                    <FontAwesomeIcon icon={faPlus} /> Add a second project
                  </button>
                )}
                <FieldError k="projects" />
              </fieldset>

              {/* rating */}
              <fieldset className="rmfb-field">
                <legend>Rate the class</legend>
                <div className="rmfb-stars" role="radiogroup" aria-label="Class rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={form.Rating === n}
                      aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      className={`rmfb-star ${n <= form.Rating ? 'is-on' : ''}`}
                      onClick={() => update({ Rating: n })}
                      disabled={submitting}
                    >
                      <FontAwesomeIcon icon={faStar} />
                    </button>
                  ))}
                </div>
                <FieldError k="rating" />
              </fieldset>
            </>
          )}

          {/* notes */}
          <div className="rmfb-field">
            <label className="rmfb-label" htmlFor="rmfb-notes">
              {completed ? 'Class notes' : 'What happened?'}
            </label>
            <textarea
              id="rmfb-notes"
              className="rmfb-input rmfb-textarea"
              value={form.Details}
              onChange={(e) => update({ Details: e.target.value })}
              placeholder={completed ? 'Progress, what was built, next steps…' : 'Explain why the class wasn’t completed…'}
              disabled={submitting}
            />
            <FieldError k="notes" />
          </div>

          {completed && (
            <>
              {/* PTM (last session only) */}
              {isLastSession && (
                <fieldset className="rmfb-field">
                  <p className="rmfb-alert">
                    <FontAwesomeIcon icon={faTriangleExclamation} /> Last session of the package: a PTM is required.
                  </p>
                  <legend>Did the PTM happen?</legend>
                  <div className="rmfb-chips">
                    {[
                      [true, 'Yes'],
                      [false, 'No'],
                    ].map(([v, label]) => (
                      <label key={label} className={`rmfb-chip ${form.PTMHappened === v ? 'is-on' : ''}`}>
                        <input
                          type="radio"
                          name="PTMHappened"
                          checked={form.PTMHappened === v}
                          onChange={() => update({ PTMHappened: v })}
                          disabled={submitting}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {form.PTMHappened && (
                    <>
                      <label className="rmfb-label" htmlFor="rmfb-ptm">
                        PTM notes
                      </label>
                      <textarea
                        id="rmfb-ptm"
                        className="rmfb-input rmfb-textarea"
                        value={form.PTMNotes}
                        onChange={(e) => update({ PTMNotes: e.target.value })}
                        placeholder="What did you discuss with the parent?"
                        disabled={submitting}
                      />
                    </>
                  )}
                  <FieldError k="ptm" />
                </fieldset>
              )}
            </>
          )}

          {/* next course (optional): chosen with the student, e.g. in the last session of a course */}
          <div className={`rmfb-field ${finishing.length ? 'rmfb-finish' : ''}`}>
            {finishing.length > 0 && (
              <p className="rmfb-finish-title">
                🎉 This class finishes <b>{finishing.join(' and ')}</b>. What’s next?
              </p>
            )}
            <label className="rmfb-label" htmlFor="rmfb-next-course">
              Next course <small>{finishing.length ? '(choose it with the student, or leave “Not chosen yet”)' : '(optional)'}</small>
            </label>
            <select
              id="rmfb-next-course"
              className="rmfb-input"
              value={nextCourse}
              onChange={(e) => setNextCourse(e.target.value)}
              disabled={submitting || loadingModules}
            >
              <option value="">{finishing.length ? 'Not chosen yet' : 'Keep as it is'}</option>
              {modules.map((m) => (
                <option key={m.Id} value={m.Id}>
                  {m.ModuleName}
                </option>
              ))}
            </select>
            <small className="rmfb-hint">
              Did the student finish a course and choose the next one with you? Pick it: their next class starts it, and the admin is told.
            </small>
          </div>

          {error && (
            <p className="rmfb-error" role="alert">
              {error}
            </p>
          )}
        </div>

        <footer className="rmfb-foot">
          <button type="button" className="rmfb-btn rmfb-btn--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="rmfb-btn" disabled={submitting}>
            {submitting ? 'Sending…' : 'Submit feedback'}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default PaidFeedbackModal;